import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Spinner from 'components/common/Spinner';
import { useTheme } from 'contexts/ThemeContext';
import { useSidebar } from 'contexts/SidebarContext';
import {
  HelpCircle,
  Search,
  ChevronRight,
  ArrowLeft,
  ListOrdered,
  Info,
  Mail,
  Menu
} from 'lucide-react';
import helpData from 'data/help/helpData.json';

interface Step {
  description: string;
  image: string;
  video: string;
}

interface Article {
  id: string;
  title: string;
  content: string;
  steps: Step[];
}

interface Category {
  id: string;
  title: string;
  icon: string;
  description: string;
  articles: Article[];
}

const Help: React.FC = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { toggleSidebar } = useSidebar();
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const categories: Category[] = helpData.categories || [];
  const supportEmail = process.env.REACT_APP_SUPPORT_EMAIL || 'support@famousclicker.com';

  // Theme tokens
  const containerBg = isDark ? '#1D293D' : '#F8FAFC';
  const mainBg = isDark ? '#0F172B' : '#FFFFFF';
  const mainShadow = isDark
    ? '0px 1px 2px rgba(2, 6, 24, 0.5)'
    : '0px 1px 2px rgba(43, 127, 255, 0.1)';
  const headerBorder = isDark ? '#1D293D' : '#F1F5F9';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const iconContainerBg = isDark ? '#1C398E' : '#EFF6FF';
  const iconColor = isDark ? '#51A2FF' : '#2B7FFF';
  const cardBg = isDark ? '#0F172B' : '#FFFFFF';
  const cardBorder = isDark ? '#314158' : '#E2E8F0';
  const sectionBorder = isDark ? '#1D293D' : '#F1F5F9';
  const inputBg = isDark ? '#0F172B' : '#FFFFFF';
  const inputBorder = isDark ? '#314158' : '#E2E8F0';
  const inputText = isDark ? '#E2E8F0' : '#314158';
  const mutedText = isDark ? '#90A1B9' : '#62748E';
  const hoverBg = isDark ? '#1D293D' : '#F8FAFC';
  const activeItemBg = isDark ? '#162456' : '#EFF6FF';
  const activeItemBorder = isDark ? '#1E3A8A' : '#BFDBFE';
  const activeItemText = isDark ? '#51A2FF' : '#2B7FFF';
  const infoBg = isDark ? '#162456' : '#EFF6FF';
  const infoBorder = isDark ? '#1E3A8A' : '#BFDBFE';
  const buttonBg = 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)';
  const stepBorderColor = isDark ? '#314158' : '#BFDBFE';

  const handleContactSupport = () => {
    const subject = encodeURIComponent('Support Request - Famous Clicker');
    const body = encodeURIComponent('Please describe your issue or question:\n\n');
    window.location.href = `mailto:${supportEmail}?subject=${subject}&body=${body}`;
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [categories]);

  const filteredCategories = categories.filter(category =>
    category.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    category.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCategoryClick = (category: Category) => {
    setSelectedCategory(category);
    setSelectedArticle(category.articles[0] || null);
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setSelectedArticle(null);
  };

  if (loading) {
    return <Spinner size="large" text={t('common.loading')} fullPage />;
  }

  return (
    <div
      className="p-0 lg:pt-2 lg:pr-2 lg:pb-2 lg:pl-0"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        height: '100%',
        gap: '8px',
        background: containerBg,
      }}
    >
      {/* Main Card */}
      <div
        className="rounded-none lg:rounded-2xl"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          flex: 1,
          width: '100%',
          overflow: 'hidden',
          background: mainBg,
          boxShadow: mainShadow,
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flex-start',
            width: '100%',
            flexShrink: 0,
            padding: '12px 16px',
            borderBottom: `1px solid ${headerBorder}`,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: '100%',
              gap: '8px',
            }}
          >
            <button
              className="lg:hidden flex items-center justify-start flex-shrink-0"
              style={{ width: '24px', height: '36px', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
              onClick={toggleSidebar}
            >
              <Menu size={24} color={isDark ? '#E2E8F0' : '#314158'} />
            </button>
            <div
              className="hidden lg:flex"
              style={{
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                width: '36px',
                height: '36px',
                background: iconContainerBg,
                borderRadius: '12px',
              }}
            >
              <HelpCircle size={24} style={{ color: iconColor }} />
            </div>
            <div style={{ flex: 1 }}>
              <h1
                style={{
                  fontFamily: 'Geist, sans-serif',
                  fontWeight: 700,
                  fontSize: '20px',
                  lineHeight: '28px',
                  color: titleColor,
                  margin: 0,
                }}
              >
                {t('help.helpCenter')}
              </h1>
            </div>

            {/* Search Bar in Header */}
            <div className="hidden sm:block" style={{ position: 'relative', width: '280px', flexShrink: 0 }}>
              <input
                type="text"
                placeholder={t('help.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px 8px 34px',
                  fontSize: '13px',
                  border: `1px solid ${inputBorder}`,
                  borderRadius: '8px',
                  background: inputBg,
                  color: inputText,
                  outline: 'none',
                  boxSizing: 'border-box',
                  height: '36px',
                }}
              />
              <Search
                size={14}
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: mutedText,
                }}
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div
          style={{
            position: 'relative',
            flex: 1,
            width: '100%',
            background: mainBg,
          }}
        >
          <div
            className={isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}
            style={{
              position: 'absolute',
              inset: 0,
              overflowY: 'auto',
              padding: '16px',
            }}
          >
            {!selectedCategory ? (
              /* Categories Grid */
              <>
                {filteredCategories.length === 0 ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '40px 16px',
                      background: cardBg,
                      border: `1px solid ${cardBorder}`,
                      borderRadius: '12px',
                    }}
                  >
                    <Search size={36} style={{ color: mutedText, marginBottom: '12px' }} />
                    <div style={{ fontSize: '14px', color: titleColor, marginBottom: '4px' }}>
                      {t('help.noArticlesFound')}
                    </div>
                    <div style={{ fontSize: '13px', color: mutedText }}>
                      {t('help.tryDifferentSearch')}
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" style={{ gap: '16px' }}>
                    {filteredCategories.map((category) => (
                      <div
                        key={category.id}
                        onClick={() => handleCategoryClick(category)}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '12px',
                          padding: '20px',
                          background: cardBg,
                          border: `1px solid ${cardBorder}`,
                          borderRadius: '12px',
                          cursor: 'pointer',
                          transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = iconColor;
                          e.currentTarget.style.boxShadow = isDark
                            ? '0px 4px 12px rgba(43, 127, 255, 0.15)'
                            : '0px 4px 12px rgba(43, 127, 255, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = cardBorder;
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            width: '36px',
                            height: '36px',
                            background: iconContainerBg,
                            borderRadius: '10px',
                          }}
                        >
                          <HelpCircle size={18} style={{ color: iconColor }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '14px', fontWeight: 600, color: titleColor, marginBottom: '4px' }}>
                            {category.title}
                          </div>
                          <div
                            style={{
                              fontSize: '13px',
                              color: mutedText,
                              marginBottom: '8px',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {category.description}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', fontWeight: 500, color: iconColor }}>
                            <span>
                              {category.articles.length} {category.articles.length !== 1 ? t('help.articles') : t('help.article')}
                            </span>
                            <ChevronRight size={14} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              /* Article View */
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Back Button */}
                <button
                  onClick={handleBackToCategories}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: mutedText,
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    transition: 'color 0.15s ease',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = iconColor)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = mutedText)}
                >
                  <ArrowLeft size={16} />
                  {t('help.backToCategories')}
                </button>

                <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr]" style={{ gap: '16px' }}>
                  {/* Articles Sidebar */}
                  <div
                    style={{
                      background: cardBg,
                      border: `1px solid ${cardBorder}`,
                      borderRadius: '12px',
                      overflow: 'hidden',
                      alignSelf: 'flex-start',
                    }}
                  >
                    {/* Sidebar Header */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 16px',
                        borderBottom: `1px solid ${sectionBorder}`,
                      }}
                    >
                      <HelpCircle size={14} style={{ color: iconColor }} />
                      <span style={{ fontSize: '13px', fontWeight: 600, color: titleColor }}>
                        {selectedCategory.title}
                      </span>
                    </div>

                    {/* Article List */}
                    <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      {selectedCategory.articles.map((article) => {
                        const isActive = selectedArticle?.id === article.id;
                        return (
                          <button
                            key={article.id}
                            onClick={() => setSelectedArticle(article)}
                            style={{
                              width: '100%',
                              textAlign: 'left',
                              padding: '8px 12px',
                              borderRadius: '8px',
                              fontSize: '13px',
                              fontWeight: isActive ? 500 : 400,
                              background: isActive ? activeItemBg : 'transparent',
                              border: isActive ? `1px solid ${activeItemBorder}` : '1px solid transparent',
                              color: isActive ? activeItemText : titleColor,
                              cursor: 'pointer',
                              transition: 'background 0.15s ease',
                            }}
                            onMouseEnter={(e) => {
                              if (!isActive) e.currentTarget.style.background = hoverBg;
                            }}
                            onMouseLeave={(e) => {
                              if (!isActive) e.currentTarget.style.background = 'transparent';
                            }}
                          >
                            {article.title}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Article Content */}
                  <div>
                    {selectedArticle && (
                      <div
                        style={{
                          background: cardBg,
                          border: `1px solid ${cardBorder}`,
                          borderRadius: '12px',
                          padding: '24px',
                        }}
                      >
                        <h2 style={{ fontSize: '16px', fontWeight: 600, color: titleColor, marginBottom: '12px' }}>
                          {selectedArticle.title}
                        </h2>
                        <p style={{ fontSize: '14px', color: titleColor, lineHeight: '1.6', marginBottom: '24px' }}>
                          {selectedArticle.content}
                        </p>

                        {/* Steps */}
                        {selectedArticle.steps.length > 0 && (
                          <div style={{ marginBottom: '24px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '16px' }}>
                              <ListOrdered size={16} style={{ color: iconColor }} />
                              <span style={{ fontSize: '14px', fontWeight: 600, color: titleColor }}>
                                {t('help.steps')}
                              </span>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                              {selectedArticle.steps.map((step, index) => (
                                <div
                                  key={index}
                                  style={{
                                    borderLeft: `2px solid ${stepBorderColor}`,
                                    paddingLeft: '16px',
                                  }}
                                >
                                  <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                                    <span
                                      style={{
                                        flexShrink: 0,
                                        width: '24px',
                                        height: '24px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '999px',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        color: '#FFFFFF',
                                        background: buttonBg,
                                      }}
                                    >
                                      {index + 1}
                                    </span>
                                    <span style={{ fontSize: '13px', color: titleColor, paddingTop: '2px', flex: 1, lineHeight: '1.5' }}>
                                      {step.description}
                                    </span>
                                  </div>

                                  {/* Step Image */}
                                  {step.image && (
                                    <div style={{ marginLeft: '34px', marginBottom: '10px' }}>
                                      <div
                                        style={{
                                          border: `1px solid ${cardBorder}`,
                                          borderRadius: '8px',
                                          overflow: 'hidden',
                                          background: isDark ? '#1D293D' : '#F8FAFC',
                                        }}
                                      >
                                        <img
                                          src={step.image}
                                          alt={t('help.stepAlt', { step: index + 1 })}
                                          style={{ width: '100%', height: 'auto', objectFit: 'contain' }}
                                          onError={(e) => {
                                            const target = e.target as HTMLImageElement;
                                            target.style.display = 'none';
                                          }}
                                        />
                                      </div>
                                    </div>
                                  )}

                                  {/* Step Video */}
                                  {step.video && (
                                    <div style={{ marginLeft: '34px' }}>
                                      <div
                                        style={{
                                          aspectRatio: '16/9',
                                          background: isDark ? '#1D293D' : '#F8FAFC',
                                          borderRadius: '8px',
                                          border: `1px solid ${cardBorder}`,
                                          overflow: 'hidden',
                                        }}
                                      >
                                        <iframe
                                          width="100%"
                                          height="100%"
                                          src={step.video.replace('youtu.be/', 'www.youtube.com/embed/').replace('watch?v=', 'embed/').split('&')[0].split('?')[0]}
                                          title={t('help.videoTutorial', { step: index + 1 })}
                                          frameBorder="0"
                                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                          allowFullScreen
                                          style={{ width: '100%', height: '100%' }}
                                        ></iframe>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Help Footer */}
                        <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: `1px solid ${sectionBorder}` }}>
                          <div
                            style={{
                              padding: '16px',
                              background: infoBg,
                              border: `1px solid ${infoBorder}`,
                              borderRadius: '10px',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                  width: '32px',
                                  height: '32px',
                                  background: isDark ? '#0F172B' : '#FFFFFF',
                                  borderRadius: '999px',
                                }}
                              >
                                <Info size={16} style={{ color: iconColor }} />
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '14px', fontWeight: 600, color: titleColor, marginBottom: '4px' }}>
                                  {t('help.stillNeedHelp')}
                                </div>
                                <div style={{ fontSize: '13px', color: mutedText, marginBottom: '12px', lineHeight: '1.5' }}>
                                  {t('help.contactSupportMessage')}
                                </div>
                                <button
                                  onClick={handleContactSupport}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '8px 16px',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    border: 'none',
                                    borderRadius: '8px',
                                    background: buttonBg,
                                    color: '#FFFFFF',
                                    cursor: 'pointer',
                                  }}
                                >
                                  <Mail size={14} />
                                  {t('help.contactSupport')}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Help;
